/**
 * Tenant Service - Handles all tenant-related API operations
 */
import axios from 'axios';
import API_CONFIG from '../config/api.config';
import { getAuthHeader } from './authService';

const API_BASE = API_CONFIG.BASE_URL;

/**
 * Register a new tenant with company and admin user
 * @param {Object} tenantData - Tenant registration data
 * @returns {Promise} Promise with tenant registration response
 */
export const registerTenant = async (tenantData) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANT_REGISTER}`,
      tenantData,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Tenant registration error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get tenant information by ID
 * @param {string} tenantId - Tenant ID
 * @returns {Promise} Promise with tenant data
 */
export const getTenant = async (tenantId) => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANTS}/${tenantId}`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get tenant error:', error);
    throw handleApiError(error);
  }
};

/**
 * Update tenant information
 * @param {string} tenantId - Tenant ID
 * @param {Object} updateData - Updated tenant data
 * @returns {Promise} Promise with updated tenant data
 */
export const updateTenant = async (tenantId, updateData) => {
  try {
    const response = await axios.put(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANTS}/${tenantId}`,
      updateData,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Update tenant error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get tenant statistics and usage data
 * @param {string} tenantId - Tenant ID
 * @returns {Promise} Promise with tenant statistics
 */
export const getTenantStatistics = async (tenantId) => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANT_STATISTICS.replace('{id}', tenantId)}`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get tenant statistics error:', error);
    throw handleApiError(error);
  }
};

/**
 * Update tenant settings
 * @param {string} tenantId - Tenant ID
 * @param {Object} settings - Settings object
 * @returns {Promise} Promise with updated tenant data
 */
export const updateTenantSettings = async (tenantId, settings) => {
  try {
    const response = await axios.put(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANT_SETTINGS.replace('{id}', tenantId)}`,
      settings,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Update tenant settings error:', error);
    throw handleApiError(error);
  }
};

/**
 * Check if subdomain is available
 * @param {string} subdomain - Subdomain to check
 * @returns {Promise} Promise with availability status
 */
export const checkSubdomainAvailability = async (subdomain) => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.CHECK_SUBDOMAIN}?subdomain=${encodeURIComponent(subdomain)}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Check subdomain availability error:', error);
    throw handleApiError(error);
  }
};

// Check if email is available (not already registered)
// @param {string} email - Email to check
// @returns {Promise} Promise with availability status
export const checkEmailAvailability = async (email) => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANTS}/check-email?email=${encodeURIComponent(email)}`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data.available;
  } catch (error) {
    console.error('Check email availability error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get all tenants (admin only)
 * @param {Object} params - Query parameters (page, size, etc.)
 * @returns {Promise} Promise with paginated tenant list
 */
export const getAllTenants = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANTS}${queryParams ? `?${queryParams}` : ''}`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get all tenants error:', error);
    throw handleApiError(error);
  }
};

/**
 * Get tenants with expiring trials (admin only)
 * @param {number} daysAhead - Days ahead to check for expiring trials
 * @param {Object} params - Query parameters
 * @returns {Promise} Promise with tenants having expiring trials
 */
export const getTenantsWithExpiringTrials = async (daysAhead = 7, params = {}) => {
  try {
    const queryParams = new URLSearchParams({ daysAhead, ...params }).toString();
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.EXPIRING_TRIALS}?${queryParams}`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Get expiring trials error:', error);
    throw handleApiError(error);
  }
};

/**
 * Suspend tenant (admin only)
 * @param {string} tenantId - Tenant ID
 * @param {string} reason - Suspension reason
 * @returns {Promise} Promise with operation result
 */
export const suspendTenant = async (tenantId, reason) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANTS}/${tenantId}/suspend`,
      null,
      {
        params: { reason },
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Suspend tenant error:', error);
    throw handleApiError(error);
  }
};

/**
 * Reactivate tenant (admin only)
 * @param {string} tenantId - Tenant ID
 * @returns {Promise} Promise with operation result
 */
export const reactivateTenant = async (tenantId) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANTS}/${tenantId}/reactivate`,
      null,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Reactivate tenant error:', error);
    throw handleApiError(error);
  }
};

/**
 * Cancel tenant
 * @param {string} tenantId - Tenant ID
 * @param {string} reason - Cancellation reason
 * @returns {Promise} Promise with operation result
 */
export const cancelTenant = async (tenantId, reason) => {
  try {
    const response = await axios.delete(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANTS}/${tenantId}`,
      {
        params: { reason },
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Cancel tenant error:', error);
    throw handleApiError(error);
  }
};

/**
 * Handle API errors consistently
 * @param {Error} error - Axios error object
 * @returns {Error} Formatted error
 */
const handleApiError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    const message = data?.message || data?.error || 'An error occurred';

    switch (status) {
      case 400:
        return new Error(`Invalid request: ${message}`);
      case 401:
        return new Error('Authentication required');
      case 403:
        return new Error('Access forbidden');
      case 404:
        return new Error('Resource not found');
      case 409:
        return new Error(`Conflict: ${message}`);
      case 500:
        return new Error('Server error. Please try again later.');
      default:
        return new Error(`Error (${status}): ${message}`);
    }
  } else if (error.request) {
    return new Error('Network error. Please check your connection.');
  } else {
    return new Error(`Request error: ${error.message}`);
  }
};

/**
 * Whether WhatsApp OTP is required for company registration (YAML + Super Admin toggle).
 */
export const getRegistrationOtpSettings = async () => {
  try {
    const response = await axios.get(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANTS}/registration-otp/settings`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Registration OTP settings error:', error);
    throw handleApiError(error);
  }
};

/**
 * Send OTP to phone number for registration verification
 * @param {string} phoneNumber - Phone number (10-15 digits)
 * @returns {Promise} Promise with send OTP response
 */
export const sendRegistrationOtp = async (phoneNumber) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANTS}/registration-otp/send`,
      { phoneNumber },
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Send OTP error:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    throw handleApiError(error);
  }
};

/**
 * Verify OTP entered by user
 * @param {string} phoneNumber - Phone number
 * @param {string} otpCode - 6-digit OTP code
 * @param {string} sessionId - Session ID from send OTP response
 * @returns {Promise} Promise with verification response
 */
export const verifyRegistrationOtp = async (phoneNumber, otpCode, sessionId) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANTS}/registration-otp/verify`,
      { phoneNumber, otpCode, sessionId },
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Verify OTP error:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    throw handleApiError(error);
  }
};

/**
 * Check if phone number is verified
 * @param {string} phoneNumber - Phone number
 * @param {string} verificationToken - Verification token (sessionId from verify response)
 * @returns {Promise} Promise with check verification response
 */
export const checkPhoneVerification = async (phoneNumber, verificationToken) => {
  try {
    const response = await axios.post(
      `${API_BASE}${API_CONFIG.ENDPOINTS.TENANTS}/registration-otp/check`,
      { phoneNumber, verificationToken },
      {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Check verification error:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    throw handleApiError(error);
  }
};

// Export all functions as default object
const tenantService = {
  registerTenant,
  getTenant,
  updateTenant,
  getTenantStatistics,
  updateTenantSettings,
  checkSubdomainAvailability,
  checkEmailAvailability,
  getAllTenants,
  getTenantsWithExpiringTrials,
  suspendTenant,
  reactivateTenant,
  cancelTenant,
  sendRegistrationOtp,
  verifyRegistrationOtp,
  checkPhoneVerification,
  getRegistrationOtpSettings
};

export default tenantService;
